import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, EyeOff, ShieldOff, Upload, Server, Sparkles, CheckCircle2 } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import HeaderWithBackButton from '@/components/common/HeaderWithBackButton';
import SEOHead from '@/components/common/SEOHead';
import { Card, CardContent } from '@/components/ui/card';
import { SITE_URL } from '@/lib/constants/urls';

const pillars = [
  {
    icon: MapPin,
    title: 'Stays in Europe',
    body: 'Your paper sits on servers physically located inside the European Union. It is never copied to servers in the US, China, or anywhere else.',
  },
  {
    icon: EyeOff,
    title: 'Only you decide who sees your draft',
    body: 'Private uploads stay hidden from other users, our team and search engines — unless you explicitly invite a named collaborator or reviewer.',
  },
  {
    icon: ShieldOff,
    title: 'Never used to train AI',
    body: 'Your manuscript is read by the AI to write your review. It is never added to a training set or used to improve any model.',
  },
];

const journey = [
  { icon: Upload, title: 'You upload', body: 'Your paper enters our system over an encrypted connection.' },
  { icon: Server, title: 'Stored in the EU', body: 'It is saved on European servers, accessible only to you.' },
  { icon: Sparkles, title: 'Reviewed in the EU', body: 'An AI hosted inside the EU reads it and writes your feedback.' },
  { icon: CheckCircle2, title: 'Returned to you', body: 'The review appears on your screen. The paper never leaves Europe.' },
];

const EUProcessing = () => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'EU Processing',
    description:
      'Your research stays in Europe — PaperVerse stores and processes your papers exclusively within the EU.',
    url: `${SITE_URL}/eu-processing`,
  };

  return (
    <>
      <SEOHead
        title="EU Processing | PaperVerse"
        description="Your research stays in Europe. PaperVerse stores and processes your papers exclusively within the European Union."
        canonicalUrl={`${SITE_URL}/eu-processing`}
        keywords="EU processing, GDPR, data residency, privacy, confidential paper review"
        structuredData={structuredData}
      />

      <div className="min-h-screen flex flex-col">
        <Navbar />

        <main className="flex-1 max-w-3xl mx-auto w-full px-4 pt-24 pb-12">
          <HeaderWithBackButton
            title="EU Processing"
            subtitle="Where your research lives — and why it's safe."
          />

          {/* The promise */}
          <p className="text-lg text-foreground/90 leading-relaxed mb-10">
            Your research stays in Europe. From the moment you upload a paper until
            the AI review appears on your screen, your manuscript is stored and
            processed inside the European Union. It never leaves.
          </p>

          {/* Three reassurance pillars */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
            {pillars.map(({ icon: Icon, title, body }) => (
              <Card key={title} className="border-border/60">
                <CardContent className="p-5 space-y-3">
                  <div className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-primary/10">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* The journey */}
          <section className="mb-12">
            <h2 className="text-xl font-semibold mb-5 text-foreground">How a review happens</h2>
            <ol className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {journey.map(({ icon: Icon, title, body }, i) => (
                <li key={title} className="relative">
                  <div className="flex md:flex-col items-start md:items-start gap-3">
                    <div className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-secondary text-secondary-foreground shrink-0">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                        Step {i + 1}
                      </p>
                      <h3 className="font-medium text-foreground text-sm mb-1">{title}</h3>
                      <p className="text-xs text-muted-foreground leading-relaxed">{body}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          {/* Honest footnote */}
          <div className="rounded-lg border border-border/60 bg-muted/30 p-5 mb-8">
            <p className="text-sm text-muted-foreground leading-relaxed">
              <span className="font-medium text-foreground">A note on helper services. </span>
              A few supporting services — like the payment page and website hosting —
              run on servers outside the EU. They never see the content of your paper.
              They only handle the minimum needed to take a payment or load the page.
            </p>
          </div>

          {/* One link for everything else */}
          <p className="text-sm text-muted-foreground">
            For the full list of providers, processing regions, and legal frameworks,
            see our{' '}
            <Link to="/privacy-policy" className="text-primary underline hover:no-underline">
              Privacy Policy
            </Link>
            .
          </p>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default EUProcessing;
